import { api } from './api'

export const usersService = {
  /*Obtiene todos los trabajadores de la empresa (filtrado por x-company-tenant-id en el header).
    tenantId: empresa distinta a la activa (ver api.js get()).*/
  async getAll(companyId, tenantId) {
    try {
      const response = await api.get('/workers', true, tenantId ? { tenantId } : {})
      return response
    } catch (error) {
      console.error('Error obteniendo trabajadores:', error)
      throw error
    }
  },

  /* Workers activos del tenant actual. El backend toma la empresa del header. */
  async getActive() {
    try {
      return await api.get('/workers?is_active=true&limit=100', true)
    } catch (error) {
      console.error('Error obteniendo trabajadores activos:', error)
      throw error
    }
  },

  /*Obtiene un trabajador por su ID*/
  async getById(workerId) {
    try {
      const response = await api.get(`/workers/${workerId}`, true)
      return response
    } catch (error) {
      console.error('Error obteniendo trabajador:', error)
      throw error
    }
  },

  /*Crea un nuevo trabajador*/
  async create(companyId, userData) {
    try {
      const payload = {
        email: userData.email.trim(),
        username: userData.username || userData.email.split('@')[0],
        full_name: (userData.full_name || userData.name || '').trim(),
        phone: userData.phone || '',
        position: userData.position || '',
        notes: userData.notes || '',
        role_name: userData.role_name || 'read',
        company_id: companyId.toString(),
        is_primary: userData.isPrimary || false
      }

      if (userData.hire_date) payload.hire_date = userData.hire_date
      if (userData.salary) payload.salary = parseFloat(userData.salary)
      if (userData.commission_rate) payload.commission_rate = parseFloat(userData.commission_rate)

      const response = await api.post('/workers', payload, true)
      return response
    } catch (error) {
      console.error('Error creando trabajador:', error)
      throw error
    }
  },

  /*Actualiza un trabajador existente*/
  async update(workerId, companyId, userData) {
    try {
      const payload = {
        full_name: (userData.full_name || userData.name || '').trim(),
        phone: userData.phone || '',
        position: userData.position || '',
        notes: userData.notes || ''
      }

      if (userData.salary) payload.salary = parseFloat(userData.salary)
      if (userData.commission_rate) payload.commission_rate = parseFloat(userData.commission_rate)
      if (userData.password) payload.password = userData.password
      if (companyId) payload.company_id = companyId.toString()

      const response = await api.put(`/workers/${workerId}`, payload, true)
      return response
    } catch (error) {
      console.error('Error actualizando trabajador:', error)
      throw error
    }
  },

  /* Asigna o actualiza el rol de un trabajador*/
  async assignRole(userId, roleName) {
    try {
      const payload = {
        role_name: roleName
      }

      const response = await api.patch(`/roles/assign/${userId}`, payload, true)
      return response
    } catch (error) {
      console.error('Error asignando rol:', error)
      throw error
    }
  },

  /*Da de baja (soft-delete) a un trabajador vía POST /workers/:id/offboard. body opcional: { appointments_action, reassign_to_worker_id }*/
  async delete(workerId, body = {}) {
    try {
      const response = await api.post(`/workers/${workerId}/offboard`, body, true)
      return response
    } catch (error) {
      console.error('Error eliminando trabajador:', error)
      throw error
    }
  },

  /*Cancela una invitación pendiente (hard delete, nunca tiene citas asociadas — no pasa por el flujo de offboard)*/
  async cancelInvitation(workerId) {
    try {
      const response = await api.delete(`/workers/${workerId}`, true)
      return response
    } catch (error) {
      console.error('Error cancelando invitación:', error)
      throw error
    }
  },

  /*Envía invitación por email*/
  async sendInvitation(workerId) {
    try {
      const response = await api.post(`/workers/${workerId}/resend-invitation`, {}, true)
      return response
    } catch (error) {
      console.error('Error enviando invitación:', error)
      throw error
    }
  },

  /*Cambia el estado de un trabajador. body opcional (solo aplica a deactivate): { appointments_action, reassign_to_worker_id }*/
  async updateStatus(workerId, status, body = {}) {
    try {
      const action = status === 'active' ? 'reactivate' : 'deactivate'
      const response = await api.patch(`/workers/${workerId}/${action}`, body, true)
      return response
    } catch (error) {
      console.error('Error actualizando estado:', error)
      throw error
    }
  },

  /*Obtiene los workers del usuario actual (empresas donde está vinculado y ha aceptado). Para perfil: "Empresas vinculadas". */
  async getMyWorkers() {
    try {
      const response = await api.get('/workers/me', true)
      const raw = response?.data ?? response
      const list = Array.isArray(raw) ? raw : (raw?.workers ?? raw?.data ?? [])
      return list
    } catch (err) {
      if (err?.status === 404) return []
      console.warn('Error obteniendo empresas vinculadas:', err?.message || err)
      return []
    }
  },

  /*Obtiene las invitaciones pendientes del usuario actual (empresas donde lo invitaron)*/
  async getPendingInvitations() {
    try {
      const response = await api.get('/workers/invitations/pending', true)
      return response
    } catch (error) {
      if (error?.status === 404) return { data: [] }
      console.error('Error obteniendo invitaciones:', error)
      throw error
    }
  },

  /*Acepta una invitación a una empresa*/
  async acceptInvitation(workerId) {
    try {
      const response = await api.patch(`/workers/${workerId}/accept`, {}, true)
      return response
    } catch (error) {
      console.error('Error aceptando invitación:', error)
      throw error
    }
  },

  /*Rechaza una invitación a una empresa*/
  async rejectInvitation(workerId) {
    try {
      const response = await api.patch(`/workers/${workerId}/reject`, {}, true)
      return response
    } catch (error) {
      console.error('Error rechazando invitación:', error)
      throw error
    }
  },

  /*Re-invita a un miembro que rechazó (vista del owner). workerId = worker.id*/
  async reinvite(workerId) {
    try {
      const response = await api.post(`/workers/${workerId}/reinvite`, {}, true)
      return response
    } catch (error) {
      console.error('Error re-invitando worker:', error)
      throw error
    }
  },

  /*Bitácora de eventos de invitación de un worker. workerId = worker.id*/
  async getInvitationEvents(workerId) {
    try {
      const response = await api.get(`/workers/${workerId}/invitation-events`, true)
      return response
    } catch (error) {
      if (error?.status === 404) return { data: [] }
      console.error('Error obteniendo bitácora de invitación:', error)
      throw error
    }
  },

  /*Elimina (soft-delete) la cuenta del usuario autenticado, confirmando su contraseña*/
  async deleteOwnAccount(password) {
    try {
      const response = await api.delete('/users/me', true, { password })
      return response
    } catch (error) {
      console.error('Error eliminando cuenta:', error)
      throw error
    }
  }
}
